/**
 * Lift2Fit — app data hooks and the one-time plan store.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiResult, GeneratedPlan } from '@/lib/api';
import { instantPaintEnabled, isFresh, loadOnce, peek, subscribe, write } from '@/lib/readCache';

export interface AsyncState<T> {
  data: T | null;
  source: 'api' | 'demo' | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * One loader hook for every dashboard. Never throws: a failed call resolves with
 * an error message the screen can announce, and the api client has already
 * fallen back to demo data where it can.
 */
export function useAsync<T>(loader: () => Promise<ApiResult<T>>, deps: ReadonlyArray<unknown>): AsyncState<T> {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const [data, setData] = useState<T | null>(null);
  const [source, setSource] = useState<'api' | 'demo' | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    loaderRef
      .current()
      .then((result) => {
        if (!alive) return;
        setSource(result.source);
        if (result.data === null || result.data === undefined) {
          setData(null);
          setError(result.problem?.detail ?? result.problem?.title ?? 'Could not load this screen.');
        } else {
          setData(result.data);
        }
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (!alive) return;
        setData(null);
        setError(cause instanceof Error ? cause.message : 'Could not load this screen.');
        setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { data, source, loading, error, reload };
}

/* ---------------------------------------------------------------------------
   One-time AI plans are handed from the wizard to the result screen by id.
   ------------------------------------------------------------------------ */

const PLAN_KEY = 'l2f.plans';
const memory = new Map<string, GeneratedPlan>();

function readAll(): Record<string, GeneratedPlan> {
  try {
    const raw = localStorage.getItem(PLAN_KEY);
    return raw ? (JSON.parse(raw) as Record<string, GeneratedPlan>) : {};
  } catch {
    return {};
  }
}

export function saveGeneratedPlan(plan: GeneratedPlan): void {
  memory.set(plan.planId, plan);
  try {
    const all = readAll();
    all[plan.planId] = plan;
    localStorage.setItem(PLAN_KEY, JSON.stringify(all));
  } catch {
    /* memory copy is enough for this session */
  }
}

export function loadGeneratedPlan(planId: string): GeneratedPlan | null {
  return memory.get(planId) ?? readAll()[planId] ?? null;
}

/**
 * Instant Paint — `useAsync` with a cache in front of it.
 *
 * With `VITE_INSTANT_PAINT` off this is `useAsync`, exactly. With it on:
 *
 *   1. a cached value for `key` paints immediately (no skeleton);
 *   2. the loader runs anyway and replaces the value when it lands ("refreshing");
 *   3. the key is subscribed, so a mutation that calls `invalidate(prefix)` makes
 *      the screen refetch without a remount.
 *
 * `stale` is true while a revalidation is in flight over painted data, so a
 * screen can say "refreshing" instead of pretending the numbers are new.
 */
export interface CachedAsyncState<T> extends AsyncState<T> {
  /** Painted from cache while a fresh request is in flight. */
  stale: boolean;
}

export function useCachedAsync<T>(
  key: string,
  loader: () => Promise<ApiResult<T>>,
  deps: ReadonlyArray<unknown>,
): CachedAsyncState<T> {
  const plain = useAsync(loader, deps);
  const enabled = instantPaintEnabled();
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const [cached, setCached] = useState<T | null>(() => {
    if (!enabled) return null;
    const entry = peek<T>(key);
    return isFresh(entry) ? (entry?.value ?? null) : null;
  });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const entry = peek<T>(key);
    if (isFresh(entry) && entry !== null) setCached(entry.value);
    return subscribe(key, () => {
      setRefreshing(true);
      void loadOnce(key, () => loaderRef.current()).finally(() => setRefreshing(false));
    });
  }, [enabled, key]);

  useEffect(() => {
    if (!enabled) return;
    if (plain.data !== null && plain.data !== undefined) write(key, plain.data);
  }, [enabled, key, plain.data]);

  if (!enabled) {
    return { ...plain, stale: false };
  }

  const data = plain.data ?? cached;
  return {
    ...plain,
    data,
    // A cached paint is not "loading": the screen has something to show.
    loading: plain.loading && data === null,
    stale: data !== null && (plain.loading || refreshing),
  };
}
